import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { map, scan } from 'rxjs/operators';
import { DeightonSpinnerComponent } from '../d-spinner.component';

/**
 * Injectable spinner/loader service
 */
@Injectable({
  providedIn: 'root'
})
export class DeightonSpinnerService {
  /**
   * Injected overlay service
   */
  private overlay: Overlay = inject(Overlay);
  /**
   * Overlay ref of the parent of the mat progress spinner.
   */
  private _spinnerOverlayRef: OverlayRef = this._createSpinnerWithOverlay();

  /**
   * The subject for spinner that we can use as subscription.
   */
  private _spinner$: Subject<boolean> = new Subject();

  /**
   * Starts the spinner and adds the overlay.
   */
  public startSpinner(): void {
    this._spinner$.next(true);
  }

  /**
   * Stops the spinner and removed the overlay.
   */
  public stopSpinner(): void {
    this._spinner$.next(false);
  }

  /**
   * Initialize overlay.
   * @param overlay The overlay for mat progress spinner.
   */
  constructor() {
    this._spinner$
      .asObservable()
      .pipe(
        map((val: boolean): number => (val ? 1 : -1)),
        scan(
          (accumulator: number, value: number): number =>
            accumulator + value >= 0 ? accumulator + value : 0,
          0
        )
      )
      .subscribe((result: number): void => {
        if (result === 1) {
          this._attachSpinner();
        } else if (result == 0) {
          if (this._spinnerOverlayRef.hasAttached()) {
            this._removeSpinner();
          }
        }
      });
  }

  /**
   * Create spinner
   */
  private _createSpinnerWithOverlay(): OverlayRef {
    return this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically()
    });
  }

  /**
   * Attach the spinner
   */
  private _attachSpinner(): void {
    // An OverlayRef only allows a component portal to exist within itself. This check if a portal is already attached is required
    // other a "Host already has a portal attached" error will be thrown.
    if (!this._spinnerOverlayRef.hasAttached()) {
      this._spinnerOverlayRef.attach(new ComponentPortal(DeightonSpinnerComponent));
    }
  }

  /**
   * Remove the spinner
   */
  private _removeSpinner(): void {
    if (this._spinnerOverlayRef.hasAttached()) {
      this._spinnerOverlayRef.detach();
    }
  }
}
